import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Card, Button, Spinner, Alert, ListGroup, Modal } from 'react-bootstrap';
import { getFace, deleteFace } from '../api';
import { useAccessToken } from '../hooks/useAccessToken';

function FaceDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [face, setFace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const getToken = useAccessToken();

  useEffect(() => {
    const fetchFace = async () => {
      try {
        setLoading(true);
        const token = await getToken();
        const data = await getFace(id, token);
        setFace(data);
      } catch (err) {
        setError(err.message || 'Failed to fetch face details.');
      } finally {
        setLoading(false);
      }
    };
    fetchFace();
  }, [id, getToken]);

  const handleDelete = async () => {
    try {
      setLoading(true);
      const token = await getToken();
      await deleteFace(id, token);
      navigate('/'); // Redirect to gallery after deletion
    } catch (err) {
      setError(err.message || 'Failed to delete face.');
    } finally {
      setLoading(false);
      setShowDeleteModal(false);
    }
  };

  if (loading) {
    return (
      <Container className="text-center my-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading face details...</span>
        </Spinner>
        <p>Loading face details...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="my-5">
        <Alert variant="danger">Error: {error}</Alert>
        <Button variant="secondary" onClick={() => navigate('/')}>
          Back to Gallery
        </Button>
      </Container>
    );
  }

  if (!face) {
    return (
      <Container className="my-5">
        <Alert variant="warning">Face not found.</Alert>
        <Button variant="secondary" onClick={() => navigate('/')}>
          Back to Gallery
        </Button>
      </Container>
    );
  }

  return (
    <Container className="my-4">
      <Card className="shadow-lg">
        <Card.Img variant="top" src={face.imageUrl} alt={face.name} style={{ maxHeight: '400px', objectFit: 'cover' }} />
        <Card.Body>
          <Card.Title className="display-4 mb-3">{face.name}</Card.Title>
          <ListGroup variant="flush">
            {face.position && <ListGroup.Item><strong>Position:</strong> {face.position}</ListGroup.Item>}
            {face.organization && <ListGroup.Item><strong>Organization:</strong> {face.organization}</ListGroup.Item>}
            {face.email && <ListGroup.Item><strong>Email:</strong> <a href={`mailto:${face.email}`}>{face.email}</a></ListGroup.Item>}
            {face.sector && <ListGroup.Item><strong>Sector:</strong> {face.sector}</ListGroup.Item>}
            {face.linkedin_url && (
              <ListGroup.Item>
                <strong>LinkedIn:</strong>{' '}
                <a href={face.linkedin_url} target="_blank" rel="noopener noreferrer">
                  {face.linkedin_url}
                </a>
              </ListGroup.Item>
            )}
            {face.how_met && <ListGroup.Item><strong>How I met them:</strong> {face.how_met}</ListGroup.Item>}
            {face.general_notes && <ListGroup.Item><strong>Notes:</strong> {face.general_notes}</ListGroup.Item>}
            <ListGroup.Item><strong>Added:</strong> {new Date(face.created_at).toLocaleDateString()}</ListGroup.Item>
            <ListGroup.Item><strong>Last Updated:</strong> {new Date(face.updated_at).toLocaleDateString()}</ListGroup.Item>
          </ListGroup>

          {face.meetings && face.meetings.length > 0 && (
            <>
              <h5 className="mt-4 mb-3">Meetings ({face.meetings.length})</h5>
              <ListGroup variant="flush">
                {face.meetings.map((meeting) => (
                  <ListGroup.Item key={meeting.id}>
                    <div>
                      <strong>{new Date(meeting.meeting_date).toLocaleDateString()}</strong>
                      {meeting.meeting_notes && (
                        <p className="mb-0 mt-1 text-muted">{meeting.meeting_notes}</p>
                      )}
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </>
          )}
          <div className="d-flex justify-content-between mt-4">
            <Button variant="secondary" onClick={() => navigate('/')}>
              Back to Gallery
            </Button>
            <div className="d-flex gap-2">
              <Button variant="info" onClick={() => navigate(`/edit/${face.id}`)}>
                Edit
              </Button>
              <Button variant="danger" onClick={() => setShowDeleteModal(true)}>
                Delete
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete {face.name}'s record?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default FaceDetails;